import { useEffect, useMemo, useState } from 'react'
import { LIST_PRINT_SETTINGS } from '../data/listPrintSettings'
import { getPreference, savePreference } from '../api/preferences'

type Props = {
  open: boolean
  onClose: () => void
}

export function PrintSettingsModal({ open, onClose }: Props) {
  const s = LIST_PRINT_SETTINGS
  const rows = s.rows

  const initialChecked = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const r of rows) map[r.id] = r.checked
    return map
  }, [])

  const initialLabels = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of rows) map[r.id] = r.label
    return map
  }, [])

  const initialPriority = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of rows) map[r.id] = r.priority
    return map
  }, [])

  const [sortingBy, setSortingBy] = useState<string>(s.sortingBy)
  const [sortingType, setSortingType] = useState<string>(s.sortingType)
  const [pageSetup, setPageSetup] = useState<string>(s.pageSetup)
  const [orientation, setOrientation] = useState<string>(s.orientation)
  const [hfRequired, setHfRequired] = useState(!!s.hfRequired)
  const [isCenterLogo, setIsCenterLogo] = useState(!!s.isCenterLogo)
  const [header, setHeader] = useState<string>(s.header)
  const [footer, setFooter] = useState<string>(s.footer)
  const [checked, setChecked] = useState(initialChecked)
  const [labels, setLabels] = useState(initialLabels)
  const [priority, setPriority] = useState(initialPriority)
  const [logoName, setLogoName] = useState<string>(s.logoName)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!open) return
    void getPreference<{
      sortingBy: string; sortingType: string; pageSetup: string; orientation: string
      hfRequired: boolean; isCenterLogo: boolean; header: string; footer: string
      checked: Record<string, boolean>; labels: Record<string, string>; priority: Record<string, string>
    }>('clients.print').then((saved) => {
      if (!saved) return
      setSortingBy(saved.sortingBy)
      setSortingType(saved.sortingType)
      setPageSetup(saved.pageSetup)
      setOrientation(saved.orientation)
      setHfRequired(saved.hfRequired)
      setIsCenterLogo(saved.isCenterLogo)
      setHeader(saved.header)
      setFooter(saved.footer)
      setChecked(saved.checked)
      setLabels(saved.labels)
      setPriority(saved.priority)
    })
  }, [open])

  if (!open) return null

  const selectedCount = Object.values(checked).filter(Boolean).length

  const onSave = async () => {
    await savePreference('clients.print', {
      sortingBy, sortingType, pageSetup, orientation, hfRequired, isCenterLogo,
      header, footer, checked, labels, priority,
    })
    setMsg('Print Settings updated successfully.')
    setTimeout(() => {
      setMsg('')
      onClose()
    }, 700)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__head">
          <h2 id="print-settings-title">Client Print Settings</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-panel__body">
          <section className="fs-print-section">
            <h3>Report Sorting</h3>
            <div className="ps-form-grid">
              <label className="ps-field">
                <span>Sorting By</span>
                <select
                  value={sortingBy}
                  onChange={(e) => setSortingBy((e.target as HTMLSelectElement).value)}
                >
                  {s.sortingByOptions.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                <span>Sorting Type</span>
                <select
                  value={sortingType}
                  onChange={(e) => setSortingType((e.target as HTMLSelectElement).value)}
                >
                  {s.sortingTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                <span>Page Setup</span>
                <select
                  value={pageSetup}
                  onChange={(e) => setPageSetup((e.target as HTMLSelectElement).value)}
                >
                  {s.pageSetupOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                <span>Orientation</span>
                <select
                  value={orientation}
                  onChange={(e) => setOrientation((e.target as HTMLSelectElement).value)}
                >
                  {s.orientationOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="fs-print-section">
            <h3>Report Print Settings</h3>
            <label className="ps-check">
              <input
                type="checkbox"
                checked={hfRequired}
                onChange={(e) => setHfRequired((e.target as HTMLInputElement).checked)}
              />
              Header &amp; Footer required
            </label>
            <label className="ps-field">
              <span>Logo</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => setLogoName((event.target as HTMLInputElement).files?.[0]?.name || '')}
              />
              <small className="muted">(Image must be of size 100px × 100px)</small>
              {logoName ? (
                <span className="ps-logo-name">
                  {logoName}{' '}
                  <button type="button" className="linkish" onClick={() => setLogoName('')}>
                    Remove
                  </button>
                </span>
              ) : null}
            </label>
            <label className="ps-check">
              <input
                type="checkbox"
                checked={isCenterLogo}
                onChange={(e) => setIsCenterLogo((e.target as HTMLInputElement).checked)}
              />
              Is Logo In center
            </label>
            <label className="ps-field">
              <span>Header</span>
              <textarea
                rows={4}
                value={header}
                onChange={(e) => setHeader((e.target as HTMLTextAreaElement).value)}
              />
            </label>
            <label className="ps-field">
              <span>Footer</span>
              <textarea
                rows={4}
                value={footer}
                onChange={(e) => setFooter((e.target as HTMLTextAreaElement).value)}
              />
            </label>
          </section>

          <section className="fs-print-section">
            <h3>Report Column Labels</h3>
            <table className="fs-print-table">
              <thead>
                <tr>
                  <th />
                  <th>Label name</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!checked[r.id]}
                        onChange={(e) =>
                          setChecked((prev) => ({
                            ...prev,
                            [r.id]: (e.target as HTMLInputElement).checked,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="ps-label-input"
                        type="text"
                        value={labels[r.id] || ''}
                        onChange={(e) =>
                          setLabels((prev) => ({
                            ...prev,
                            [r.id]: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="fs-priority-input"
                        type="text"
                        value={priority[r.id] || ''}
                        onChange={(e) =>
                          setPriority((prev) => ({
                            ...prev,
                            [r.id]: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <div className="modal-panel__foot">
          {msg ? (
            <span className="muted">{msg}</span>
          ) : (
            <span className="muted">{selectedCount} columns selected</span>
          )}
          <div style={{ flex: '1' }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void onSave()}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
